/**
 * usePmsNewTaskNotifier — Batch #14.212
 *
 * Hook qui :
 *   1. Détecte une augmentation du compteur PMS (nouveau ticket arrivé)
 *   2. Joue un petit son (data:audio inline, pas de fichier externe)
 *   3. Affiche une notification browser (Notification API) si permission accordée
 *
 * Le compteur de référence est `counts.sidebar.pms` (tâches à traiter de la Boîte de réception PMS).
 * S'auto-déclenche dès que la nouvelle valeur > l'ancienne mémorisée.
 *
 * Utilisation (dans AdminLayout) :
 *   const { counts } = useBadgeCounts();
 *   usePmsNewTaskNotifier(counts);
 */
import { useEffect, useRef } from "react";

// Petit "ding" base64 (WAV très court, ~3 ko) — son neutre
const DING_DATA_URL = "data:audio/wav;base64,UklGRhYGAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YfIFAACAgIB/f4B/f39/gICAgH9/f4CAgICAgH9/f4CAgIB/f39/f3+AgIB/f39/gICAgH9/f4CAgICAf39/gICAgH9/f3+AgICAf39/gIB/f4CAgIB/f3+AgIB/f4CAgIB/f3+AgICAf39/f3+AgICAf39/gICAgIB/f3+AgICAf39/gICAgH9/f4CAgIB/f39/f4CAgIB/f3+AgICAf39/gICAgIB/f3+AgICAf39/gICAgH9/f3+AgIB/f3+AgICAf39/gICAgIB/f3+AgICAf39/f4CAgIB/f3+AgICAf39/gICAgH9/f3+AgICAf39/gICAgIB/f3+AgICAgH9/gICAgH9/f4CAgIB/f3+AgIB/f4CAgIB/f3+AgICAf3+AgIB/f3+AgIB/f4CAgIB/f3+AgICAf39/gICAgH9/f4CAgIB/f3+AgICAf3+AgICAf39/gICAgH9/f4CAgIB/f3+AgICAf39/gICAgH9/f4CAgIB/f3+AgICAf39/gICAgIB/f4CAgIB/f3+AgIB/f4CAgIB/f3+AgICAf3+AgIB/f3+AgIB/f4CAgIB/f4CAgIB/f3+AgICAf39/gICAgIB/f4CAgIB/f3+AgICAf39/gICAgIB/f3+AgICAf3+AgICAf39/gICAgIB/f3+AgICAf39/gICAgH9/f4CAgIB/f3+AgICAf39/gICAgIB/f3+AgICAf39/gICAgH9/f3+AgIB/f3+AgIB/f4CAgIB/f4CAgIB/f3+AgIB/f3+AgIB/f4CAgIB/f3+AgICAf39/gICAgH9/f4CAgIB/f3+AgICAf3+AgICAf39/gICAgIB/f3+AgICAf39/gICAgIB/f3+AgICAf39/gICAgH9/f4CAgIB/f3+AgICAf3+AgIB/f4CAgIB/f3+AgIB/f4CAgIB/f3+AgICAf39/gICAgH9/f4CAgIB/f3+AgICAf39/gICAgH9/f4CAgIB/f3+AgICAf39/gICAgH9/f3+AgIB/f3+AgIB/f4CAgIB/f3+AgICAf3+AgIB/f3+AgICAf39/gICAgH9/f4CAgIB/f3+AgICAf3+AgIB/f3+AgICAf39/gICAgH9/f4CAgIB/f3+AgICAf39/gICAgH9/f4CAgIB/f3+AgICAf39/gICAgH9/f4CAgIB/f3+AgICAf39/gICAgIB/f3+AgICAf39/gICAgIB/f3+AgICAf39/gICAgIB/f3+AgIB/f4CAgIB/f3+AgICAf39/gICAgIB/f3+AgICAgH9/f4CAgIB/f3+AgICAf39/gICAgIB/f3+AgICAf39/gICAgIB/f3+AgICAf39/gICAgIB/f3+AgICAf39/gICAgIB/f3+AgICAf39/gICAgIB/f4CAgIB/f3+AgICAf39/gICAgIB/f3+AgICAf3+AgIB/f3+AgIB/f4CAgIB/f3+AgICAf39/gICAgIB/f3+AgICAf3+AgIB/f3+AgICAf39/gICAgIB/f4CAgIB/f3+AgICAf3+AgIB/f3+AgICAf39/gICAgIB/f4CAgIB/f3+AgICAf39/gICAgIB/f3+AgICAf39/gICAgIB/f3+AgICAf39/gICAgIB/f4CAgIB/f3+AgICAf39/gICAgIB/f3+AgIB/f4CAgIB/f3+AgIB/f4CAgIB/f3+AgICAf3+AgIB/f3+AgIB/f4CAgIB/f3+AgIB/f4CAgIB/f3+AgIB/f4CAgIB/f3+AgIB/f4CAgIB/f3+AgIB/f4CAgIB/f3+AgIB/";

let dingAudio = null;
let audioBlocked = false;

function playDing() {
  try {
    if (!dingAudio) dingAudio = new Audio(DING_DATA_URL);
    dingAudio.volume = 0.4;
    dingAudio.currentTime = 0;
    dingAudio.play().catch(() => {
      audioBlocked = true;
    });
  } catch (e) {
    // Bloqué par le navigateur — c'est ok
  }
}

function showBrowserNotification(title, body) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  try {
    const n = new Notification(title, {
      body,
      icon: "/favicon.ico",
      tag: "pms-new-task",
      renotify: true,
    });
    n.onclick = () => {
      window.focus();
      window.location.href = "/admin/pms-queue";
      n.close();
    };
    setTimeout(() => n.close(), 8000);
  } catch (e) {
    // Ignore
  }
}

export default function usePmsNewTaskNotifier(counts) {
  const lastSeenRef = useRef(null);

  useEffect(() => {
    if (counts?.sidebar?.pms === undefined) return;
    const current = counts.sidebar.pms || 0;
    if (lastSeenRef.current === null) {
      // Premier render — on initialise, pas de notif
      lastSeenRef.current = current;
      return;
    }
    if (current > lastSeenRef.current) {
      const delta = current - lastSeenRef.current;
      playDing();
      showBrowserNotification(
        "Nouvelle tâche PMS",
        `${delta} nouvelle${delta > 1 ? "s" : ""} demande${delta > 1 ? "s" : ""} dans la boîte de réception.`,
      );
    }
    lastSeenRef.current = current;
  }, [counts?.sidebar?.pms]); // eslint-disable-line react-hooks/exhaustive-deps

  // Demande la permission au premier mount (geste utilisateur recommandé mais on tente quand même)
  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "default") {
      // On tente une demande passive — le navigateur peut ignorer si pas d'interaction utilisateur
      Notification.requestPermission().catch(() => {});
    }
  }, []);
}

export { audioBlocked };
